/**
 * Feature-flag registries. A flag is just a string stored on a row; these
 * registries name the known flags, describe them, and give them compile-time
 * keys so a typo can't be persisted.
 *
 * There are three grains, and picking the right one matters:
 *
 * - **INSTANCE flags** (`FEATURE_FLAGS` → `instances.feature_flags`, V173) gate
 *   how ONE AGENT behaves. Use these for agent/runtime behaviour: a new tool, a
 *   different scheduling rule, an A/B treatment a hire is assigned at creation.
 *   Toggled at /admin/instances/<id>.
 * - **USER flags** (`USER_FEATURE_FLAGS` → `users.feature_flags`, V297) gate
 *   what ONE PERSON sees or may do across the product, independent of which
 *   agent they're looking at. Use these for account-level concerns: billing
 *   surfaces, entitlements, early access to a page. Toggled at
 *   /admin/users/<id>.
 * - **ORGANIZATION flags** (`ORG_FEATURE_FLAGS` → `organizations.feature_flags`,
 *   V391) gate what ONE ORGANIZATION — every member and every agent it owns —
 *   gets. Use these for capabilities that are inherently about a group of
 *   agents or people under one roof: agents working together, an org-wide
 *   integration, a pilot sold to a company rather than to a person. Toggled at
 *   /admin/organizations/<id>.
 *
 * A user owns many instances, so anything whose answer must not change when
 * they switch agents belongs on the USER registry — putting a billing decision
 * on an instance flag makes "may this person buy X?" depend on which agent you
 * happened to ask. And an organization has many users, so anything that must
 * hold for ALL of them at once — or that an agent needs to know about its
 * colleagues, not itself — belongs on the ORGANIZATION registry: set per user
 * it would be on for some members and off for others, and set per instance an
 * agent could be "collaborating" with a colleague that is not.
 *
 * Neither is the mechanism for anonymous-visitor A/B tests — those are
 * deterministic cookie assignments in apps/web/lib/experiments/, with no row to
 * flip.
 *
 * Retiring a flag: delete its registry entry once the behaviour is
 * unconditional. Rows may still carry the string; nothing reads it. Leave a
 * note at the bottom of this file saying what graduated and when.
 */

export interface FeatureFlag {
  readonly key: string;
  readonly description: string;
}

// ── Instance flags — instances.feature_flags ────────────────────────────────

export const FEATURE_FLAGS = [
  {
    key: "compiled_scheduled_tasks_off",
    description:
      "KILL SWITCH. Compiled scheduled tasks are on for every agent since GA (2026-09-23); tick this to take THIS agent off them. Compiled form means: after enough convergent runs the nightly dream distills a recurring calendar event into an explicit procedure stored on the event, and subsequent fires execute it on a cheaper model with a lean context (the procedure + recent channel updates + only the tools it names) instead of the full frontier turn. Runs escalate back to the full model on anomaly, and any edit to the event marks the procedure stale. Ticked, every scheduled fire runs the full path; already-compiled procedures stay stored but are not executed, and the nightly compiler leaves the agent alone. Use it when an agent's compiled runs are misbehaving and you want it off without reverting the fleet — COMPILED_SCHEDULED_TASKS_DISABLED is the env-level switch for that.",
  },
  {
    key: "memory_lib_shadow",
    description:
      "Runs the next-generation memory library (memory.* schema) alongside this agent's existing observation tier \u2014 capture, nightly consolidation and recall all happen, but WRITE-AND-LOG only: nothing it produces reaches the agent's context. The prerequisite for the two flags below, and safe on its own: a library failure can never affect a turn. Costs roughly one Haiku pair per turn plus a nightly consolidation.",
  },
  {
    key: "memory_lib_live",
    description:
      "Serves this agent's recalled-memories block FROM the library instead of the observation tier \u2014 replace mode, so the library is authoritative for what the agent remembers. Requires memory_lib_shadow, and is ignored without it: the library is only kept current for shadowed agents, so serving from an unshadowed one would hand the agent a frozen memory. Reverting is safe while the observation tier still runs in parallel, which it does until that tier is retired.",
  },
  {
    key: "memory_lib_dream",
    description:
      "Renders this agent's .agent/mission.md and .agent/personality.md as PROJECTIONS of the library's facts each night, instead of free-writing them from observation-tier reflections. Requires memory_lib_live, and is ignored without it \u2014 a persona written from facts the agent's own recall cannot return would split it across two memories. The pre-existing file is preserved once at <file>.pre-lib.bak before the first render, so turning this off and restoring is possible.",
  },
  {
    key: "platform_ops",
    description:
      "Gives this agent the platform-operations tools an internal agent (a Nora) needs to work incident reviews: today `sentiment_review`, the worklist over user-sentiment readings — list readings by score and window and mark them handled with a disposition and a reference, exactly as an admin does on /admin/sentiment. It never exposes the sealed summary text; that stays a human-on-the-admin-page read. Internal instances only.",
  },
  {
    key: "transcription_biasing",
    description:
      "Pilot gate for phrase biasing on the OpenAI transcription path (voice notes, web dictation, the `transcribe` tool). When on, this agent's name plus a short product vocabulary (media/transcription-vocabulary.ts) is sent as the model's `prompt`, so product nouns stop being mis-heard — on a Swedish test passage every error any model made was one (the agent's own name came back as Kim, Sken, Shin), and biasing took it to 0%. Gated because it changes transcription for every current customer and a Whisper-family prompt can leak into the transcript. The EU (Google chirp_2) path is biased unconditionally and ignores this flag. Remove the flag once the pilot shows no regression.",
  },
  {
    key: "auth_handoff",
    description:
      "Pilot gate for `request_user_auth` (Browsor auth handoff, phase 3): when the agent reaches a sign-in only the person can finish (BankID, a code sent to their phone), it asks them in a card in their own web chat, waits for their Ready, and watches its browser until the sign-in shows up. A person who is not looking at the chat gets a web push. Web chat only; without the flag the tool is not in the agent's list at all.",
  },
  {
    key: "accounted",
    description:
      "Gate for the `accounted` catalog skill while only the person who registered our callback at Accounted, and people sharing a company with them, can sign in there (docs/provider-apps.md). When on, this agent is shown the skill. The `mcp` tool can reach Accounted either way; the flag only decides who is told about it. Remove the flag, and name Accounted again in the discovery prompt and the ops persona, once Accounted has made our callback a built-in redirect.",
  },
] as const satisfies readonly FeatureFlag[];

export type FeatureFlagKey = (typeof FEATURE_FLAGS)[number]["key"];

// ── User flags — users.feature_flags ────────────────────────────────────────

export const USER_FEATURE_FLAGS = [
  {
    key: "starter_plan_offer",
    description:
      "Offers the withdrawn Starter tier (149 SEK / month) on /billing alongside Pro and Max. For an account we had personally quoted that price before withdrawing the tier from sale on 2026-08-26 — a promise being kept, not a tier coming back. Existing Starter subscribers need nothing set.",
  },
  {
    key: "browser_companion",
    description:
      "Pilot of the browser companion (.plans/prd-browser-companion.md): this person can connect their own Chrome through the Squidler extension (/companion/connect) and let the agents they choose read the tab they are looking at. Shows the browser section in Settings. Removing it stops new connections; browsers already connected keep working until revoked there.",
  },
] as const satisfies readonly FeatureFlag[];

export type UserFeatureFlagKey = (typeof USER_FEATURE_FLAGS)[number]["key"];

// ── Organization flags — organizations.feature_flags ────────────────────────

export const ORG_FEATURE_FLAGS = [
  {
    key: "vitec",
    description:
      "Pilot gate for the Vitec Express Mäklarsystem integration. When on, every agent in this organisation may connect a Vitec office (the realtor tile in its integrations), gets the `vitec` tool once an office is connected (any status), surfaces the `vitec` catalog skill, and is woken by the office's webhooks. Per organisation because a real-estate firm buys it for its agents, not one agent at a time. The deployment must also hold a partner credential. Remove the flag at GA and let credentials + a connection be the only gate.",
  },
  {
    key: "a2a_external_colleagues",
    description:
      "Pilot gate for A2A external colleagues: an agent outside Squidler (first, a customer's Microsoft Copilot Studio agent) may ask one of this organisation's agents for work over the Agent2Agent protocol, on a connection an org admin or the agent's owner draws on the organisation's agent graph. When on, the graph offers \"Connect an outside agent\", and the endpoint at squidler.io/a2a/<connection> answers this organisation's connections. When off, nothing is offered and every such connection is refused exactly like an unknown one (existing connections are kept, not closed). Per organisation because the company that runs Copilot is the unit that adopts it.",
  },
] as const satisfies readonly FeatureFlag[];

export type OrgFeatureFlagKey = (typeof ORG_FEATURE_FLAGS)[number]["key"];

// ── Lookup ─────────────────────────────────────────────────────────────────

// A type guard over one registry's keys. Generic in the key union rather than
// reading it off the tuple, so an EMPTY registry (all flags retired, element
// type `never`) still type-checks — the widened parameter type accepts it.
const keyPredicate = <K extends string>(registry: readonly FeatureFlag[]) => {
  const known: ReadonlySet<string> = new Set(registry.map((f) => f.key));
  return (key: string): key is K => known.has(key);
};

/** True when `key` is one of the defined INSTANCE feature flags. */
export const isFeatureFlagKey = keyPredicate<FeatureFlagKey>(FEATURE_FLAGS);

/** True when `key` is one of the defined USER feature flags. */
export const isUserFeatureFlagKey = keyPredicate<UserFeatureFlagKey>(USER_FEATURE_FLAGS);

/** True when `key` is one of the defined ORGANIZATION feature flags. */
export const isOrgFeatureFlagKey = keyPredicate<OrgFeatureFlagKey>(ORG_FEATURE_FLAGS);

/** True when `key` is present in the row's enabled flags. Grain-agnostic — the
 *  storage shape is identical for instances, users and organizations. */
export const hasFeature = (flags: readonly string[], key: string): boolean => flags.includes(key);

/**
 * System boundary for an ADMIN-supplied flag array (the per-entity toggles):
 * only keys `isKnown` accepts survive, trimmed and deduped, so the persisted
 * array is always a subset of that grain's registry. Grain-agnostic — callers
 * pass their grain's predicate.
 *
 * `undefined` means "the request didn't ask to change flags" and is distinct
 * from `[]`, which means "clear them" — an admin turning off the last flag must
 * not be read as having sent nothing. So a non-array (missing field, garbage,
 * an array with a non-string in it) returns undefined and the caller leaves the
 * column alone, rather than silently wiping an entitlement.
 *
 * Not for visitor-supplied input: the hire flow's `cleanInitialFeatureFlags`
 * (apps/web/lib/hire-flags.ts) deliberately maps garbage to `[]` instead.
 */
export const cleanFeatureFlags = (
  raw: unknown, isKnown: (key: string) => boolean,
): string[] | undefined => {
  if (!Array.isArray(raw) || !raw.every((flag) => typeof flag === "string")) return undefined;
  return [...new Set(raw.map((flag) => flag.trim()).filter(isKnown))];
};

// ── Graduated / retired flags ──────────────────────────────────────────────
//
// The referral program (formerly the `referrals` USER pilot flag, 2026-10-27 →
// GA 2026-11-03, V535): everyone sees the Referrals page, its menu entry and
// the /account and /billing cards, and anyone in a paying organization may
// invite (`may_invite`). Rows may still carry the flag string harmlessly;
// nothing reads it.
//
// The `api` tool (formerly the `api_tooling` pilot flag, 2026-10-09 → GA
// 2026-10-22): every agent gets load/search/describe/validate/patch_spec and
// the `working-with-apis` skill. Signing in to an OAuth API through it
// (formerly the `api_connect` pilot flag, 2026-10-20 → GA 2026-10-23) came
// with it: `api connect`, `connections`, `request_client_registration`,
// `own_app`, the `signing-in-to-apis` skill and Settings → Other APIs. Rows
// may still carry either flag string harmlessly; nothing reads them.
//
// Video and music (formerly the `media_generation` ORGANIZATION pilot flag,
// 2026-10-10 → GA, merged no earlier than 2026-10-26, the day the
// `google-cloud-media` sub-processor entry takes effect): every organisation
// on Global data residency gets `generate_video` and `generate_music`; EU
// residency is the only gate left (media-generation/access.ts). Rows may still
// carry the flag string harmlessly; nothing reads it.
//
// Speech through an <audio> element (formerly the `speech_media_element`
// experiment, 2026-10-19 → deleted 2026-10-21): routing read-aloud audio
// through a hidden <audio> element instead of straight from Web Audio, to
// keep it playing in the background on Android. A real phone showed no
// difference, so it was deleted rather than made unconditional, along with
// the background-playback diagnostics it shipped with. Rows may still carry
// the flag string harmlessly; nothing reads it.
//
// Vitec as an INSTANCE flag (2026-09 → 2026-10-15): the pilot moved to the
// ORGANIZATION grain above (V487 copied each flagged instance's pilot onto its
// organization). Instance rows may still carry the string harmlessly; nothing
// reads it.
//
// Agents working together (formerly the `agent_collaboration` ORGANIZATION
// pilot flag, 2026-09 → GA 2026-10-07): every organization's agents may work
// together along connections a person drew or approved; having another active
// agent in the organization is the only gate. Released after the queue and the
// ceiling were made to tell the sender the truth (#1434) and to deliver a held
// backlog as one (#1467, #1469). Rows may still carry the flag string
// harmlessly; nothing reads it.
//
// Agent voice (formerly the `agent_voice` ORGANIZATION pilot flag, 2026-10-09
// → GA 2026-10-19): every agent reads its replies aloud in the web chat — the
// play control on each reply, "Read replies aloud", the voice setting and the
// agent's `voice` tool — for every organization. Rows may still carry the flag
// string harmlessly; nothing reads it.
//
// Organizations (formerly the `organizations` USER flag): the surfaces for
// choosing BETWEEN organizations — the "where should this agent live?" step
// when hiring and the org badge in the agent switcher — are shown to everyone
// with more than one destination. Organizations had been GA for a while before
// the flag was retired (2026-09-28); until then those surfaces stayed hidden
// from everyone but flagged accounts. Rows may still carry the flag string
// harmlessly; nothing reads it.
//
// Oqto accounting (formerly the `oqto` pilot flag, 2026-09-24 → GA
// 2026-09-25): the deployment holding an Oqto application plus a connected
// company is the whole gate, as for Fortnox and Bokio. Rows may still carry
// the flag string harmlessly; nothing reads it.
//
// Structured turn context (formerly the `structured_turn_context` A/B flag,
// 2026-09-16, made unconditional 2026-09-17): every agent's per-turn context
// is framed with XML-style tags (<turn-context>, <recalled-…>, <user-message>)
// — the `[System: …]` bracket convention is gone from the runtime. Old rows
// may still carry the flag string harmlessly; nothing reads it.
//
// Fire-time notification routing (formerly the `notification_routing` flag,
// rolled out 2026-07 and then made unconditional): non-user-initiated messages
// resolve the user's main channel at FIRE time (Telegram/Slack → push-capable
// web → email), scheduled web deliveries are visibility-aware, and a delivery
// that reaches no trusted (mobile) push device gets an email copy. This is now
// simply how delivery works — no flag, no legacy path.
//
// Shared group web chat (formerly the `web_group_chat` flag, retired 2026-07):
// the group chat is now generally available for every agent — being an
// `instance_members` member is the only gate. Old rows may still carry the
// flag string harmlessly; nothing reads it.
//
// Device location sharing from the web chat (formerly the `web_location_sharing`
// flag, retired 2026-07): PWA location capture, gateway location_update
// ingestion, and the place tool's follow/request_location + geofence web source
// are now unconditional on personal web channels for every agent. Old rows may
// still carry the flag string harmlessly; nothing reads it.
//
// Proactive onboarding engagement (formerly the proactive_onboarding A/B
// treatment flag, made unconditional 2026-07): the tapered early-days morning
// outreach (days 1-14) and the self-review morning-follow-up suppression during
// that window are now how every agent behaves — no flag, no cohort. Old instance
// rows may still carry the flag string harmlessly; nothing reads it.
//
// Fortnox and Bokio accounting integrations (formerly the `fortnox` and `bokio`
// flags, GA 2026-09-08): both are offered to every agent whose deployment has
// the provider's OAuth credentials configured. The tile, the tool and the skill
// are now gated only on what they were always really gated on — credentials
// existing, and the instance having a connected company — so an agent with no
// connection still has no tool, exactly as before. Old rows may still carry the
// flag strings harmlessly; nothing reads them.
//
// Programmatic tool calling (formerly the `tool_script` flag, GA 2026-09-04):
// every instance gets the tool. The flag had already stopped being read when
// the pilot concluded — this only removes the registry entry that outlived it.
// `TOOL_SCRIPT_DISABLED=true` remains the fleet-wide kill switch, and
// `PTC_MAX_CONCURRENT_SCRIPTS` bounds concurrency; neither is per instance.
//
// Compiled scheduled tasks (formerly the `compiled_scheduled_tasks` flag, GA
// 2026-09-23): every agent's recurring calendar events are eligible for
// compilation. The positive flag is replaced by `compiled_scheduled_tasks_off`
// above rather than simply retired — this feature swaps the model and the whole
// context on a recurring fire, so the per-instance escape hatch stays. Old rows
// may still carry the positive string harmlessly; nothing reads it.
// `COMPILED_SCHEDULED_TASKS_DISABLED=true` is the fleet-wide kill switch.
//
// Custom domains for deployed apps (formerly the `custom_domains` flag, GA
// 2026-10-21): every agent's apps on their own address can be served on the
// owner's own hostname — the deploy tool's add_domain / check_domain /
// remove_domain and the "Own domain" section on the app page. The procedure
// the tool description carried for flagged agents is now in the developer
// skill ("Custom domains"). Old rows may still carry the flag string
// harmlessly; nothing reads it.
//
// Spiris accounting integration (formerly the `spiris` organization flag, GA
// 2026-10-23): offered to every agent whose deployment holds the Spiris OAuth
// client, like Fortnox, Bokio and Oqto. The tile, the tool, the skill and the
// web routes are gated on credentials and a connected company only. Old rows
// may still carry the flag string harmlessly; nothing reads it.
